import axios from "axios";
import { apiUrlBase } from "../../data/urls";
import returnAuthHeaderForAJAX from "../auth/returnAuthHeaderForAJAX";

const TOLT_PROVIDER = "tolt";
const TOLT_READY_TIMEOUT_MS = 2000;

export type ToltAttribution = {
  customer_id: string;
  partner_id?: string;
  click_id?: string;
};

const captureToltFailure = (reason: string) => {
  if (window.posthog && typeof window.posthog.capture === "function") {
    window.posthog.capture("tolt_signup_tracking_failed", { reason });
  }
};

const waitForTolt = async () => {
  if (window.tolt && typeof window.tolt.signup === "function") {
    return window.tolt;
  }

  if (!window.__unicornToltReady) {
    return undefined;
  }

  await Promise.race([
    window.__unicornToltReady,
    new Promise<void>((resolve) => window.setTimeout(resolve, TOLT_READY_TIMEOUT_MS)),
  ]);

  return window.tolt && typeof window.tolt.signup === "function"
    ? window.tolt
    : undefined;
};

export const trackAffiliateSignup = async (
  email: string
): Promise<ToltAttribution | undefined> => {
  const provider = 'tolt'; // or firstpromoter

  if (provider !== TOLT_PROVIDER) {
    if (typeof window.fpr === "function") {
      window.fpr("referral", { email });
    }
    return undefined;
  }

  try {
    const tolt = await waitForTolt();
    if (!tolt) {
      captureToltFailure("sdk_unavailable");
      return undefined;
    }

    const result = await tolt.signup(email);
    const customerId = result.customer_id || (window.tolt_data && window.tolt_data.customer_id);
    if (!customerId) {
      captureToltFailure("customer_id_missing");
      return undefined;
    }
    return {
      customer_id: customerId,
      partner_id: result.partner_id || (window.tolt_data && window.tolt_data.partner_id),
      click_id: result.click_id || (window.tolt_data && window.tolt_data.click_id),
    };
  } catch (_error) {
    captureToltFailure("signup_failed");
    return undefined;
  }
};

export const saveToltAttribution = async (
  accessToken: string,
  attribution?: ToltAttribution
): Promise<void> => {
  if (!accessToken || !attribution || !attribution.customer_id) {
    return;
  }
  try {
    await axios.post(
      `${apiUrlBase}/api/v1/users/tolt-attribution`,
      attribution,
      returnAuthHeaderForAJAX(accessToken)
    );
  } catch (_error) {
    captureToltFailure("attribution_persist_failed");
  }
};
